"use client";

import { useGetCertifications } from "@/hooks/main-hooks";
import { cn } from "@/lib/utils";
import { CertificationTypes } from "@/types/main-types";
import { AnimatePresence, motion } from "framer-motion";
import { useLocale } from "next-intl";
import { useEffect, useState } from "react";
import { CertCard } from "../certifications/certification";
import { CertModal } from "../certifications/zoom";

/* ─── main component ─── */
export default function Certifications() {
  const [modalImage, setModalImage] = useState<string | null>(null);
  const locale = useLocale();
  const isAr = locale === "ar";

  const { data, isLoading } = useGetCertifications({ page: 1, limit: 500 });
  const certifications = data?.certifications || [];

  /* lock body scroll when modal open */
  useEffect(() => {
    document.body.style.overflow = modalImage ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [modalImage]);

  const handleOpen = (img: string) => setModalImage(img);
  const handleClose = () => setModalImage(null);

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: "#FAFAFD", paddingTop: 96, paddingBottom: 120 }}
    >
      {/* ── dot grid texture ── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle, #DDDDE8 1px, transparent 1px)`,
          backgroundSize: "32px 32px",
          opacity: 0.5,
        }}
      />

      {/* ── fade top & bottom ── */}
      <div
        className="absolute inset-x-0 top-0 h-36 pointer-events-none"
        style={{
          background: "linear-gradient(to bottom, #FAFAFD, transparent)",
        }}
      />
      <div
        className="absolute inset-x-0 bottom-0 h-36 pointer-events-none"
        style={{ background: "linear-gradient(to top, #FAFAFD, transparent)" }}
      />

      <div className="container mx-auto px-6 relative z-10">
        {/* ── Header ── */}
        <div className={cn("mb-20", isAr ? "text-right" : "text-left")}>
          {/* eyebrow pill */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: "#EDEDF5",
              borderRadius: 99,
              padding: "6px 16px",
              marginBottom: 22,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "#1A1A2E",
                display: "block",
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "0.2em",
                color: "#1A1A2E",
                textTransform: "uppercase",
              }}
            >
              {isAr ? "الاعتمادات الدولية" : "Global Accreditations"}
            </span>
          </motion.div>

          {/* main headline with outline text */}
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            style={{
              fontFamily: "'Syne', sans-serif",
              fontSize: "clamp(2.6rem, 5.5vw, 5rem)",
              fontWeight: 800,
              color: "#1A1A2E",
              lineHeight: 1.02,
              letterSpacing: "-0.04em",
              maxWidth: 640,
              ...(isAr ? { marginLeft: "auto" } : {}),
            }}
          >
            {isAr ? (
              <>
                توثيق{" "}
                <span
                  style={{
                    WebkitTextStroke: "2px #1A1A2E",
                    color: "transparent",
                  }}
                >
                  الإبداع
                </span>{" "}
                والجودة
              </>
            ) : (
              <>
                Documenting{" "}
                <span
                  style={{
                    WebkitTextStroke: "2px #1A1A2E",
                    color: "transparent",
                  }}
                >
                  Excellence
                </span>
              </>
            )}
          </motion.h2>

          {/* subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 16,
              color: "#7A7A90",
              lineHeight: 1.7,
              marginTop: 18,
              maxWidth: 480,
              ...(isAr ? { marginLeft: "auto" } : {}),
            }}
          >
            {isAr
              ? "شهاداتنا تعكس التزامنا الراسخ بالجودة والتميز في كل ما نقدمه."
              : "Our certifications reflect an unwavering commitment to quality and global standards."}
          </motion.p>
        </div>

        {/* ── 3-col card grid ── */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {isLoading
            ? Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse rounded-3xl"
                  style={{ height: 420, background: "#EDEDF5" }}
                />
              ))
            : certifications.map((cert: CertificationTypes, idx: number) => (
                <CertCard
                  key={cert.id}
                  cert={cert}
                  idx={idx}
                  isAr={isAr}
                  onOpen={handleOpen}
                />
              ))}
        </div>
      </div>

      {/* ── Modal ── */}
      <AnimatePresence>
        {modalImage && (
          <CertModal image={modalImage} isAr={isAr} onClose={handleClose} />
        )}
      </AnimatePresence>
    </section>
  );
}
